import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { ApiError, api, TransportError } from '../api/client.js';
import { useAuth } from '../auth/AuthProvider.js';
import { useI18n } from '../i18n/I18nProvider.js';
import { Alert, Button } from '../ui/index.js';

type WaitingList = Awaited<ReturnType<typeof api.get<'/api/v1/waiting'>>>;
type Commitment = WaitingList['youOwe'][number];

/**
 * Who is waiting for whom.
 *
 * Two lists of the same shape meaning opposite things, and the quote under each line is the point:
 * telling somebody they promised something is a claim about their own words, so the words are
 * there to be checked. A line whose quote looks nothing like a promise is a line to dismiss, and
 * dismissing it should cost one click.
 *
 * Marking something done is the common case rather than the exception — most promises are kept in
 * a phone call or a corridor, somewhere the mail never sees, and a list that can only be settled
 * by an arriving email is a list that only grows.
 */
export function WaitingScreen(): ReactNode {
  const { t, translator } = useI18n();
  const { activeWorkspace } = useAuth();

  const [list, setList] = useState<WaitingList | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [resolving, setResolving] = useState<string | null>(null);

  const tenantId = activeWorkspace?.id;

  const load = useCallback(
    async (signal?: AbortSignal) => {
      if (tenantId === undefined) return;
      try {
        const result = await api.get('/api/v1/waiting', undefined, { tenantId, signal });
        setList(result);
        setFailure(null);
      } catch (error) {
        if (signal?.aborted === true) return;
        setList(null);
        setFailure(
          error instanceof ApiError
            ? error.code
            : error instanceof TransportError
              ? 'UNREACHABLE'
              : 'INTERNAL_ERROR',
        );
      }
    },
    [tenantId],
  );

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const resolve = useCallback(
    async (commitmentId: string, outcome: 'kept' | 'cancelled') => {
      if (tenantId === undefined) return;
      setResolving(commitmentId);
      try {
        await api.post(
          '/api/v1/waiting/{commitmentId}/resolve',
          { outcome },
          { commitmentId },
          {
            tenantId,
          },
        );
        /* Removed here rather than reloaded: the row is gone from the list either way, and
           re-fetching two hundred rows to drop one is a page that flickers for no reason. */
        setList((current) =>
          current === null
            ? null
            : {
                youOwe: current.youOwe.filter((item) => item.commitmentId !== commitmentId),
                owedToYou: current.owedToYou.filter((item) => item.commitmentId !== commitmentId),
              },
        );
      } catch {
        setFailure('INTERNAL_ERROR');
      } finally {
        setResolving(null);
      }
    },
    [tenantId],
  );

  const section = (title: string, items: Commitment[], emptyKey: string): ReactNode => (
    <section className="card">
      <h2 className="card__title">
        {title} {items.length > 0 && <span className="thread__count">{items.length}</span>}
      </h2>
      {items.length === 0 ? (
        <p className="empty__body">{t(emptyKey)}</p>
      ) : (
        <ul className="today-list">
          {items.map((item) => (
            <li className="today-list__item" key={item.commitmentId}>
              {item.expectedAt !== null && (
                <span className="badge">
                  {translator.formatDate(new Date(item.expectedAt))}
                  {item.expectedSource === 'default' && ` · ${t('waiting.assumed')}`}
                </span>
              )}
              <span className="today-list__subject">{item.summary}</span>
              {item.quote !== null && item.quote !== '' && (
                /* The sentence it came from. Without this the screen is a machine asserting
                   things about somebody's promises with nothing to back it. */
                <span className="thread__snippet">“{item.quote}”</span>
              )}
              <span className="today-list__meta">
                {[item.counterparty, translator.formatDate(new Date(item.createdAt))]
                  .filter((part) => part !== null && part !== '')
                  .join(' · ')}
              </span>
              <span className="mailbox-form__actions">
                <Button
                  disabled={resolving !== null}
                  onClick={() => void resolve(item.commitmentId, 'kept')}
                >
                  {t('waiting.done')}
                </Button>
                <Button
                  disabled={resolving !== null}
                  onClick={() => void resolve(item.commitmentId, 'cancelled')}
                >
                  {t('waiting.dismiss')}
                </Button>
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );

  if (failure !== null && list === null) {
    return (
      <Alert tone="critical" title={t('waiting.error.title')}>
        <Button onClick={() => void load()}>{t('today.retry')}</Button>
      </Alert>
    );
  }

  if (list === null) {
    return (
      <div className="boot" role="status" aria-live="polite">
        {t('common.loading')}
      </div>
    );
  }

  return (
    <>
      <header className="greeting">
        <h1 className="greeting__title">{t('waiting.title')}</h1>
        <p className="greeting__summary">{t('waiting.subtitle')}</p>
      </header>

      {section(t('waiting.owedToYou'), list.owedToYou, 'waiting.owedToYou.empty')}
      {section(t('waiting.youOwe'), list.youOwe, 'waiting.youOwe.empty')}
    </>
  );
}
